<template>
	<thead class="table-head">
		<tr
			class="table-head-row"
			:class="{ 'show-owner': canPerformProfileSelector }"
		>
			<th scope="col" class="table-head__checkbox">
				<CheckBox
					aria-label="Select all contacts on the current page"
					:checked="isCurrentPageSelected"
					@onClick="handleSelectCurrentPage"
				/>
			</th>
			<th
				v-for="(columnHeader, index) in columnHeaders"
				scope="col"
				:class="`table-head__${columnHeader.className}`"
				:data-test="columnHeader.className"
				:key="index"
				@click="handleSort(columnHeader.value)"
			>
				{{ columnHeader.name }}
				<font-awesome-icon
					v-if="
						sortDirection === 'asc' &&
						sortColumn === columnHeader.value
					"
					class="sort-down-icon"
					data-test="sort-down"
					:icon="['fad', 'sort-down']"
				/>
				<font-awesome-icon
					v-else-if="
						sortDirection === 'desc' &&
						sortColumn === columnHeader.value
					"
					class="sort-up-icon"
					data-test="sort-up"
					:icon="['fad', 'sort-up']"
				/>
				<font-awesome-icon v-else :icon="['fas', 'sort']" />
			</th>
			<th scope="col" class="table-head__quick-actions">
				<span class="quick-actions">
					<QuickActionsMenu
						data-test="multiple-quick-actions-menu"
						isMultiple
						lightButton
						:showCounter="showCounter"
						:disabled="!showCounter"
						:lightButtonIcon="['far', 'ellipsis-h']"
						:menus="populateMultipleUserQuickActions()"
						:selectedContacts="selectedContacts"
						@sendToVendor="showVendorModal()"
						@showChangeMultiContactsModal="
							handleChangeContactTypeModal()
						"
						@showMultipleDeactivateModal="
							handleShowDeactivateModal()
						"
						@showMultipleReactivateModal="
							handleShowReactivateModal()
						"
						@showMultiTransferModal="handleShowTransferModal()"
					/>
				</span>
			</th>
		</tr>
	</thead>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { CONTACTS } from '../constants';
import { VIEWS as MODAL_VIEWS } from '../modals/constants';

import CheckBox from '../CheckBox.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';

export default {
	name: 'ContactTableHeader',
	components: {
		CheckBox,
		QuickActionsMenu,
	},
	props: {
		activeContacts: Boolean,
		currentPageLeadIds: Array,
		selectAllMode: Boolean,
	},
	computed: {
		...mapGetters([
			'canDeleteContact',
			'canReassignLeadsToDifferentContact',
			'isImpersonated',
		]),
		...mapState({
			// ccContacts
			selectedLeadIds: (state) => state.ccContacts.leads.selectedLeadIds,
			sortColumn: (state) => state.ccContacts.views.sortColumn,
			sortDirection: (state) => state.ccContacts.views.sortDirection,
			totalCount: (state) => state.ccContacts.leads.totalCount,
			// profile
			showVendorLinkQuickAction: (state) => !!state.profile.bigMacLink,
			// profile switcher
			canPerformProfileSelector: (state) =>
				state.profileSwitcher.mainProfileSelector
					.canPerformProfileSelector,
			columnHeaders: (state) => {
				let headers = CONTACTS.TABLE_HEADERS;
				if (
					!state.profileSwitcher.mainProfileSelector
						.canPerformProfileSelector
				) {
					headers = headers.filter((h) => h.name !== 'Owner');
				}
				return headers;
			},
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		isCurrentPageSelected() {
			if (this.selectAllMode) {
				return !this.currentPageLeadIds.every((id) =>
					this.selectedLeadIds.includes(id),
				);
			} else {
				return this.currentPageLeadIds.every((id) =>
					this.selectedLeadIds.includes(id),
				);
			}
		},
		selectedContacts() {
			if (this.selectAllMode) {
				return this.totalCount - this.selectedLeadIds.length;
			} else {
				return this.selectedLeadIds.length;
			}
		},
		showCounter() {
			return this.selectedContacts > 1;
		},
	},
	methods: {
		...mapActions([
			'setModalView',
			'setRemoveModalBackCaret',
			'setSelectedLeadIds',
			'setShowOverviewModal',
			'setSortColumn',
			'setSortDirection',
		]),
		handleSelectCurrentPage(selected) {
			if (this.selectAllMode === selected) {
				this.setSelectedLeadIds(
					this.selectedLeadIds.filter(
						(id) => !this.currentPageLeadIds.includes(id),
					),
				);
			} else {
				this.setSelectedLeadIds([
					...new Set([
						...this.selectedLeadIds,
						...this.currentPageLeadIds,
					]),
				]);
			}
		},
		async handleSort(column) {
			if (this.sortColumn !== column) {
				this.setSortColumn(column);
				this.setSortDirection('asc');
			} else {
				this.setSortDirection(
					this.sortDirection === 'asc' ? 'desc' : 'asc',
				);
			}
			this.$emit('getSortedContacts');
		},
		populateMultipleUserQuickActions() {
			if (!this.activeContacts) {
				return [
					[
						{
							icon: ['far', 'recycle'],
							iconColor: 'green',
							text: 'Reactivate Contacts',
							action: 'showMultipleReactivateModal',
							disabled:
								this.mainProfileIsInternalUser ||
								!this.canDeleteContact,
						},
					],
				];
			}
			let menu = [
				[
					{
						icon: ['fas', 'user-cog'],
						text: 'Change Contact Type',
						action: 'showChangeMultiContactsModal',
						disabled: this.mainProfileIsInternalUser,
					},
				],
				[
					{
						icon: ['far', 'exchange'],
						text: 'Transfer Contacts',
						action: 'showMultiTransferModal',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canReassignLeadsToDifferentContact,
					},
				],
				[
					{
						icon: ['far', 'trash-alt'],
						iconColor: 'red',
						text: 'Mark Contacts Inactive',
						action: 'showMultipleDeactivateModal',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canDeleteContact,
					},
				],
			];

			if (this.showVendorLinkQuickAction) {
				menu[0].push({
					icon: ['far', 'file-import'],
					text: 'Send Vendor Link',
					action: 'sendToVendor',
					disabled:
						this.mainProfileIsInternalUser || this.isImpersonated,
				});
			}

			return menu;
		},
		handleChangeContactTypeModal() {
			this.setModalView(MODAL_VIEWS.UPDATE_CONTACT_TYPE);
			this.setShowOverviewModal(true);
		},
		handleShowTransferModal() {
			this.setRemoveModalBackCaret(true);
			this.setModalView(MODAL_VIEWS.TRANSFER_CONTACT);
			this.setShowOverviewModal(true);
		},
		handleShowDeactivateModal() {
			this.setRemoveModalBackCaret(true);
			this.setModalView(MODAL_VIEWS.DEACTIVATE_CONTACT);
			this.setShowOverviewModal(true);
		},
		handleShowReactivateModal() {
			this.setRemoveModalBackCaret(true);
			this.setModalView(MODAL_VIEWS.REACTIVATE_CONTACT);
			this.setShowOverviewModal(true);
		},
		showVendorModal() {
			this.setModalView(MODAL_VIEWS.SEND_TO_VENDOR);
			this.setShowOverviewModal(true);
		},
	},
};
</script>

<style scoped lang="scss">
.table-head__quick-actions {
	justify-self: end;

	/deep/ .btn-quick-actions {
		@include rem('width', 44px);
		@include rem('height', 44px);
	}
}

.sort-up-icon {
	@include rem('margin-bottom', 2px);
}

.sort-down-icon {
	@include rem('margin-bottom', -4px);
}

.table-head-row {
	@include rem('padding', 2.5px 15px 2.5px 20px);
	display: grid;
	grid-template-columns: 4% repeat(2, 1fr) repeat(2, 12%) 16% 44px;
	gap: 15px;
	justify-items: start;
	align-items: center;
	&.show-owner {
		@media only screen and (min-width: 1201px) {
			grid-template-columns: 5% repeat(2, 1fr) repeat(3, 15%) 5%;
		}
	}
	background: $header-color-light;
}

.quick-actions {
	cursor: default;
}

@media only screen and (max-width: 549px) {
	.table-head-row {
		grid-template-columns: 8% 1fr 44px;
	}

	.table-head__email,
	.table-head__type,
	.table-head__phone,
	.table-head__owner {
		display: none;
	}
}

@media only screen and (min-width: 550px) and (max-width: 659px) {
	.table-head-row {
		grid-template-columns: 5% repeat(2, 1fr) 44px;
	}

	.table-head__type,
	.table-head__phone,
	.table-head__owner {
		display: none;
	}
}

@media only screen and (min-width: 660px) and (max-width: 1049px) {
	.table-head-row {
		grid-template-columns: 5% repeat(3, 1fr) 44px;
	}

	.table-head__type,
	.table-head__owner {
		display: none;
	}
}

@media only screen and (min-width: 1050px) and (max-width: 1200px) {
	.table-head-row {
		grid-template-columns: 5% repeat(4, 1fr) 44px;
	}

	.table-head__owner {
		display: none;
	}
}
</style>